import { ResultOf } from "@/config/graphql";
import { useMutation, useQuery } from "@apollo/client";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  addProductionDetailMutation,
  productionResourcesQuery,
  removeProductionDetailMutation,
  updateProductionDetailMutation,
} from "./ProductionReportForm.queries";
import { ChangeEvent } from "react";
import { formatTextNumber } from "@/utils/number";

const Content = styled(Box)`
  padding: 16px 24px;
`;
const Form = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

type DestinationOption = ResultOf<
  typeof productionResourcesQuery
>["destinationOptions"][number];
function isDestinationOption(arg: unknown): arg is DestinationOption {
  if (typeof arg !== "object" || arg === null) return false;
  if (!("name" in arg)) return false;
  if (!("code" in arg)) return false;
  return true;
}

function formatDestinationOption(option: DestinationOption) {
  if (!option.code) return option.name!;
  return `${option.name} [${option.code}]`;
}

const productionDetailSchema = z.object({
  id: z.number().optional(),
  resourceId: z.number().gt(0, "Seleccione un tipo de mezcla"),
  sellQuantity: z.preprocess(
    (v) => +formatTextNumber(String(v)),
    z.number().gt(0, "Ingrese un monto de venta")
  ),
  destinationName: z.preprocess((arg) => {
    if (typeof arg === "string") return arg;
    if (isDestinationOption(arg)) {
      if (arg.id < 0) return arg.name;
      return formatDestinationOption(arg);
    }
  }, z.string({ required_error: "Seleccione un destino" })),
});
type ProductionDetailSchema = z.infer<typeof productionDetailSchema>;
interface ProductionReportFormProps {
  close: () => void;
  defaultValues?: Partial<ProductionDetailSchema>;
  destinationDetail?: string;
}
export const ProductionReportForm = (props: ProductionReportFormProps) => {
  const params = useParams();
  const { data, loading } = useQuery(productionResourcesQuery, {
    variables: { params: { id: +(params.id || 0) } },
  });
  const [addProductionDetail, { loading: adding }] = useMutation(
    addProductionDetailMutation
  );
  const [updateProductionDetail, { loading: updating }] = useMutation(
    updateProductionDetailMutation
  );
  const [removeProductionDetail, { loading: removing }] = useMutation(
    removeProductionDetailMutation,
    {
      variables: { params: { id: +(props.defaultValues?.id || 0) } },
    }
  );
  const mutating = adding || updating || removing;
  const { control, handleSubmit } = useForm<ProductionDetailSchema>({
    resolver: zodResolver(productionDetailSchema),
    defaultValues: props.defaultValues,
  });

  const handleClose = () => props.close();
  const handleRemove = async () => {
    await removeProductionDetail();
    handleClose();
  };
  const onSubmit = async (formData: ProductionDetailSchema) => {
    const destination = data?.destinationOptions.find(
      (option) =>
        formatDestinationOption(option) === formData.destinationName &&
        option.id > 0
    );
    const destinationData = {
      destinationId: destination?.id,
      destinationDetail: !destination ? formData.destinationName : undefined,
      destinationName: undefined,
    };
    if (formData.id) {
      await updateProductionDetail({
        variables: {
          params: {
            ...formData,
            id: formData.id,
            ...destinationData,
          },
        },
      });
    } else {
      await addProductionDetail({
        variables: {
          params: {
            ...formData,
            reportId: +(params.id || 0),
            ...destinationData,
          },
        },
      });
    }
    handleClose();
  };

  const options = data?.destinationOptions ?? [];

  return (
    <>
      <Content>
        <Typography variant="h6">Agregar Mezcla</Typography>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="resourceId"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                select
                label="Tipo de Mezcla"
                fullWidth
                InputProps={{
                  endAdornment: loading && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                {...field}
                value={field.value || ""}
              >
                <MenuItem value={0} disabled></MenuItem>
                {data?.report.plant?.productionResources?.map((resource) => (
                  <MenuItem key={resource.id} value={resource.id}>
                    {resource.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="sellQuantity"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Cantidad"
                fullWidth
                type="text"
                onWheel={() =>
                  (document.activeElement as HTMLInputElement).blur()
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">ton</InputAdornment>
                  ),
                  inputProps: { inputMode: "decimal" },
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                {...field}
                value={field.value || ""}
              ></TextField>
            )}
          />

          <Controller
            name="destinationName"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Autocomplete
                  options={options}
                  value={field.value}
                  onChange={(_, value) => {
                    field.onChange(value);
                  }}
                  freeSolo
                  groupBy={(option) =>
                    option.type === "internal" ? "Internos" : "Externos"
                  }
                  getOptionLabel={(param) => {
                    if (typeof param === "string") return param;
                    return formatDestinationOption(param);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Destino"
                      fullWidth
                      inputProps={{
                        ...params.inputProps,
                        onChange: (e: ChangeEvent<HTMLInputElement>) => {
                          params.inputProps.onChange?.(e);
                          field.onChange(e.target.value);
                        },
                      }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                ></Autocomplete>
              </>
            )}
          />
        </Form>
      </Content>
      <DialogActions sx={{ padding: "8px 24px" }}>
        {props.defaultValues?.id ? (
          <>
            <Button disabled={mutating} onClick={handleRemove} color="error">
              ELIMINAR
              {removing && (
                <CircularProgress size={20} sx={{ marginLeft: 1 }} />
              )}
            </Button>
            <Button
              disabled={mutating}
              onClick={handleSubmit(onSubmit)}
              color="info"
            >
              GUARDAR
              {updating && (
                <CircularProgress size={20} sx={{ marginLeft: 1 }} />
              )}
            </Button>
          </>
        ) : (
          <>
            <Button disabled={mutating} onClick={handleClose} color="error">
              CANCELAR
            </Button>
            <Button
              disabled={mutating}
              onClick={handleSubmit(onSubmit)}
              color="info"
            >
              GUARDAR
              {adding && <CircularProgress size={20} sx={{ marginLeft: 1 }} />}
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );
};
